/**
 * Composer @mention / #channel picker, shared by the main thread composer and
 * the thread reply composer.
 *
 * - `@` offers staff ("People"), members ("Members", staff composers only) and
 *   @everyone; picking inserts `@[Name](user:12) ` or `@[Name](member:34) `.
 * - `#` at a word boundary offers channels the caller can see; picking inserts
 *   `#[general](channel:5) `. A `#word` that is not picked stays a hashtag.
 *
 * Suggestions come from GET /api/messaging/mentionables (debounced, cached per
 * query). If that endpoint is missing or fails, the picker falls back to the
 * organisation staff list (for `@`) and the caller's channel list (for `#`).
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Hash, Lock, Users } from 'lucide-react';
import {
  messagingApi,
  Channel,
  MentionableType,
  Mentionables,
} from '@/services/messagingApi';
import { userManagementAPI, OrganizationUser } from '@/services/api/userManagement';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';

export type MentionTrigger = '@' | '#';

export interface ActiveTrigger {
  trigger: MentionTrigger;
  /** Index of the trigger character in the draft. */
  start: number;
  /** Text typed after the trigger, up to the caret. */
  query: string;
}

export type MentionOption =
  | { kind: 'user'; id: number; label: string; sublabel: string | null }
  | { kind: 'member'; id: number; label: string; memberNumber: string | null }
  | { kind: 'broadcast'; value: 'everyone' }
  | { kind: 'channel'; id: number; name: string; label: string; isPrivate: boolean };

export const MENTION_DEBOUNCE_MS = 150;
const SECTION_LIMIT = 8;
const FALLBACK_STAFF_LIMIT = 5;

/**
 * The `@` / `#` the caret is currently completing, or null.
 *
 * `@` must not follow a word character ("me@example.org" is an email); `#` must
 * start the text or follow whitespace / an opening bracket, so "a#b", "&#39;"
 * and "https://x.org/#top" never open the channel picker.
 */
export function findMentionTrigger(text: string, cursor: number): ActiveTrigger | null {
  const before = text.slice(0, Math.max(0, cursor));
  const start = Math.max(before.lastIndexOf('@'), before.lastIndexOf('#'));
  if (start === -1) return null;
  const trigger = before.charAt(start) as MentionTrigger;
  const query = before.slice(start + 1);
  const prev = start > 0 ? before.charAt(start - 1) : '';
  if (trigger === '@') {
    if (!/^\w*$/.test(query)) return null;
    if (/\w/.test(prev)) return null;
    return { trigger, start, query };
  }
  if (!/^[\w-]*$/.test(query)) return null;
  if (prev && !/[\s([{]/.test(prev)) return null;
  return { trigger, start, query };
}

/** Names go inside `[...]`, so a `]` or line break would end the markup early. */
function markupName(name: string | null | undefined): string {
  const cleaned = (name || '').replace(/[\]\r\n]/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned || '?';
}

const isId = (id: unknown): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0;

/** The text a picked option inserts (without the trailing space). */
export function mentionMarkup(option: MentionOption): string {
  switch (option.kind) {
    case 'user':
      return `@[${markupName(option.label)}](user:${option.id})`;
    case 'member':
      return `@[${markupName(option.label)}](member:${option.id})`;
    case 'channel':
      return `#[${markupName(option.name)}](channel:${option.id})`;
    case 'broadcast':
    default:
      return '@everyone';
  }
}

interface UseMentionComposerArgs {
  inputRef: React.RefObject<HTMLTextAreaElement>;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  channelId?: number;
  /** Offer members under `@` (staff composers). False for member-portal viewers. */
  includeMembers: boolean;
}

export interface MentionComposer {
  active: ActiveTrigger | null;
  open: boolean;
  people: MentionOption[];
  members: MentionOption[];
  broadcasts: MentionOption[];
  channels: MentionOption[];
  /** All visible options, in keyboard order. */
  options: MentionOption[];
  index: number;
  setIndex: (i: number) => void;
  /** Call from the textarea's onChange (after updating the draft). */
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Call from onKeyDown; returns true when the picker consumed the key. */
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  select: (option: MentionOption) => void;
  /** The @ button: insert `@` at the caret and open the picker. */
  openAtCaret: () => void;
  close: () => void;
}

export function useMentionComposer({
  inputRef,
  setValue,
  channelId,
  includeMembers,
}: UseMentionComposerArgs): MentionComposer {
  const [active, setActive] = useState<ActiveTrigger | null>(null);
  const [index, setIndex] = useState(0);
  const [debounced, setDebounced] = useState<{ trigger: MentionTrigger; query: string } | null>(null);
  // Set once the mentionables endpoint fails (e.g. 404 on an older backend):
  // from then on this composer uses the staff / channel lists instead.
  const [unavailable, setUnavailable] = useState(false);

  const trigger = active?.trigger ?? null;
  const query = active?.query ?? '';
  useEffect(() => {
    if (!trigger) {
      setDebounced(null);
      return;
    }
    const timer = setTimeout(() => setDebounced({ trigger, query }), MENTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trigger, query]);

  const q = debounced?.query ?? '';
  const dTrigger = debounced?.trigger ?? null;
  const types: MentionableType[] =
    dTrigger === '#' ? ['channel'] : includeMembers && q.length >= 1 ? ['user', 'member'] : ['user'];

  const mentionables = useQuery({
    queryKey: ['mentionables', channelId ?? null, types.join(','), q],
    queryFn: async (): Promise<Mentionables & { trigger: MentionTrigger | null }> => {
      const res = await messagingApi.getMentionables({ q, channel_id: channelId, types, limit: 20 });
      return { ...res, trigger: dTrigger };
    },
    enabled: !!dTrigger && !unavailable,
    staleTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (mentionables.isError) setUnavailable(true);
  }, [mentionables.isError]);

  const cboId = typeof localStorage !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const staff = useQuery({
    queryKey: ['org-users-mentions', cboId],
    queryFn: async () => (cboId ? userManagementAPI.listUsers(parseInt(cboId, 10)) : []),
    enabled: unavailable && !!cboId && trigger === '@',
  });
  const channelList = useQuery({
    queryKey: ['messaging-channels'],
    queryFn: () => messagingApi.listChannels(),
    enabled: unavailable && trigger === '#',
  });

  const data = mentionables.data && mentionables.data.trigger === trigger ? mentionables.data : null;

  const { people, members, broadcasts, channels } = useMemo(() => {
    const empty = { people: [], members: [], broadcasts: [], channels: [] } as {
      people: MentionOption[];
      members: MentionOption[];
      broadcasts: MentionOption[];
      channels: MentionOption[];
    };
    if (!trigger) return empty;
    const needle = query.toLowerCase();
    if (trigger === '#') {
      let found: MentionOption[] = [];
      if (unavailable) {
        found = ((channelList.data || []) as Channel[])
          .filter((c) => !c.is_dm && c.channel_type !== 'direct' && c.dm_member_id == null && !c.is_archived)
          .filter((c) =>
            !needle ||
            (c.name || '').toLowerCase().includes(needle) ||
            (c.display_name || '').toLowerCase().includes(needle),
          )
          .filter((c) => isId(c.id) && !!c.name)
          .map((c) => ({
            kind: 'channel' as const,
            id: c.id,
            name: c.name,
            label: c.display_name || c.name,
            isPrivate: c.channel_type === 'private',
          }));
      } else if (data) {
        found = data.channels
          .filter((c) => isId(c.id) && !!c.name)
          .map((c) => ({
            kind: 'channel' as const,
            id: c.id,
            name: c.name,
            label: c.display_name || c.name,
            isPrivate: !!c.is_private,
          }));
      }
      return { ...empty, channels: found.slice(0, SECTION_LIMIT * 2) };
    }

    let ppl: MentionOption[] = [];
    let mem: MentionOption[] = [];
    if (unavailable) {
      ppl = ((staff.data || []) as OrganizationUser[])
        .filter((u) =>
          !needle ||
          u.name?.toLowerCase().includes(needle) ||
          u.email?.toLowerCase().includes(needle),
        )
        .filter((u) => isId(u.id))
        .slice(0, FALLBACK_STAFF_LIMIT)
        .map((u) => ({ kind: 'user' as const, id: u.id, label: u.name, sublabel: u.email || null }));
    } else if (data) {
      ppl = data.users
        .filter((u) => isId(u.id))
        .slice(0, SECTION_LIMIT)
        .map((u) => ({ kind: 'user' as const, id: u.id, label: u.name, sublabel: u.email || u.role || null }));
      if (includeMembers && query.length >= 1) {
        mem = data.members
          .filter((m) => isId(m.id))
          .slice(0, SECTION_LIMIT)
          .map((m) => ({ kind: 'member' as const, id: m.id, label: m.name, memberNumber: m.member_number || null }));
      }
    }
    const bc: MentionOption[] = [{ kind: 'broadcast', value: 'everyone' }];
    return { ...empty, people: ppl, members: mem, broadcasts: bc };
  }, [trigger, query, unavailable, data, staff.data, channelList.data, includeMembers]);

  const options = trigger === '#' ? channels : [...people, ...members, ...broadcasts];
  // Matches the old picker: it opens once there is someone (or some channel) to pick.
  const open = trigger === '#' ? channels.length > 0 : people.length + members.length > 0;

  // Caret after an insertion is applied in the same commit as the new draft
  // (layout effect), so a fast next keystroke can never land before it.
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const pos = pendingCaret.current;
    if (pos == null) return;
    pendingCaret.current = null;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      /* element not focusable in this environment */
    }
  });
  const placeCaret = (pos: number) => {
    pendingCaret.current = pos;
  };

  const select = (option: MentionOption) => {
    const current = active;
    if (!current) return;
    const insert = `${mentionMarkup(option)} `;
    const end = current.start + 1 + current.query.length;
    setValue((prev) => prev.slice(0, current.start) + insert + prev.slice(end));
    setActive(null);
    setIndex(0);
    placeCaret(current.start + insert.length);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart ?? value.length;
    setActive(findMentionTrigger(value, cursor));
    setIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!open || options.length === 0) return false;
    const count = options.length;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setIndex((i) => (i + step + count) % count);
      return true;
    }
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
      e.preventDefault();
      select(options[Math.min(index, count - 1)]);
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setActive(null);
      return true;
    }
    return false;
  };

  const openAtCaret = () => {
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? el.value.length;
    setValue((prev) => prev.slice(0, cursor) + '@' + prev.slice(cursor));
    setActive({ trigger: '@', start: cursor, query: '' });
    setIndex(0);
    placeCaret(cursor + 1);
  };

  return {
    active,
    open,
    people,
    members,
    broadcasts,
    channels,
    options,
    index,
    setIndex,
    handleChange,
    handleKeyDown,
    select,
    openAtCaret,
    close: () => setActive(null),
  };
}

function Initial({ name, tone = 'default' }: { name: string; tone?: 'default' | 'member' }) {
  return (
    <div
      className={cn(
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium',
        tone === 'member'
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
          : 'bg-primary/10',
      )}
    >
      {name?.charAt(0).toUpperCase() || '?'}
    </div>
  );
}

/** The dropdown; render inside the textarea's `relative` wrapper. */
export function MentionPicker({ composer, className }: { composer: MentionComposer; className?: string }) {
  const { t } = useAppTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const { open, options, index } = composer;

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-option-index="${index}"]`);
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [open, index]);

  // On a narrow composer the box (anchored to the message field) would run
  // past the pane's right edge and be clipped: slide it left to fit.
  const boxRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!open || !el) return;
    const anchor = el.parentElement?.getBoundingClientRect();
    const pane = el.closest('[data-composer-pane]') ?? el.closest('[data-testid="message-composer"]');
    const bounds = pane?.getBoundingClientRect();
    if (!anchor || !bounds || !bounds.width) return;
    const right = Math.min(bounds.right, window.innerWidth) - 8;
    const overflow = anchor.left + el.offsetWidth - right;
    const next = overflow > 0 ? -Math.min(overflow, Math.max(0, anchor.left - bounds.left - 8)) : 0;
    if (next !== shift) setShift(next);
  });

  if (!open) return null;

  const rowClass = (i: number) =>
    cn(
      'flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800',
      i === index && 'bg-slate-100 dark:bg-slate-800',
    );
  const heading = (label: string) => (
    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
  );
  const row = (option: MentionOption, i: number, children: React.ReactNode) => (
    <button
      key={`${option.kind}-${'id' in option ? option.id : option.value}`}
      type="button"
      role="option"
      aria-selected={i === index}
      data-option-index={i}
      data-testid={`mention-option-${option.kind}`}
      onClick={() => composer.select(option)}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={() => composer.setIndex(i)}
      className={rowClass(i)}
    >
      {children}
    </button>
  );

  let i = 0;
  const isChannels = composer.active?.trigger === '#';

  return (
    <div
      className={cn(
        'absolute bottom-full left-0 z-20 mb-1 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border bg-white shadow-lg dark:bg-slate-900',
        className,
      )}
      data-testid="mention-picker"
      ref={boxRef}
      style={shift ? { left: shift } : undefined}
    >
      <div ref={listRef} role="listbox" aria-label={isChannels ? t('messaging.mentions.channels', 'Channels') : t('messaging.mentions.mention', 'Mention')} className="max-h-64 overflow-y-auto pb-1">
        {isChannels ? (
          <>
            {heading(t('messaging.mentions.channels', 'Channels'))}
            {composer.channels.map((option) => {
              if (option.kind !== 'channel') return null;
              const at = i++;
              return row(
                option,
                at,
                <>
                  {option.isPrivate ? (
                    <Lock className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  ) : (
                    <Hash className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{option.name}</div>
                    {option.label !== option.name && (
                      <div className="truncate text-xs text-muted-foreground">{option.label}</div>
                    )}
                  </div>
                </>,
              );
            })}
          </>
        ) : (
          <>
            {composer.people.length > 0 && heading(t('messaging.mentions.people', 'People'))}
            {composer.people.map((option) => {
              if (option.kind !== 'user') return null;
              const at = i++;
              return row(
                option,
                at,
                <>
                  <Initial name={option.label} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{option.label}</div>
                    {option.sublabel && <div className="truncate text-xs text-muted-foreground">{option.sublabel}</div>}
                  </div>
                </>,
              );
            })}
            {composer.members.length > 0 && heading(t('messaging.mentions.members', 'Members'))}
            {composer.members.map((option) => {
              if (option.kind !== 'member') return null;
              const at = i++;
              return row(
                option,
                at,
                <>
                  <Initial name={option.label} tone="member" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{option.label}</span>
                      <span className="flex-shrink-0 rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
                        {t('messaging.mentions.memberBadge', 'Member')}
                      </span>
                    </div>
                    {option.memberNumber && (
                      <div className="truncate text-xs text-muted-foreground">
                        {t('messaging.mentions.memberNumber', 'Member # {{number}}', { number: option.memberNumber })}
                      </div>
                    )}
                  </div>
                </>,
              );
            })}
            <div className="mt-1 border-t pt-1">
              {composer.broadcasts.map((option) => {
                const at = i++;
                return row(
                  option,
                  at,
                  <>
                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" aria-hidden>
                      <Users className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{mentionMarkup(option)}</div>
                      <div className="text-xs text-muted-foreground">{t('messaging.composer.notifyAll', 'Notify all members')}</div>
                    </div>
                  </>,
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
